<template>
  <modal title="Ship Quote" height="450px" size="xl" dialog-class="ship-quote-dialog">
    <template #top>
      <template v-if="!results">
        <modal-row label="Quote">
          <div class="d-flex flex-wrap align-items-center pt-1">
            <b-form-radio-group v-model="mode" class="mr-3"
                                :options="[{value: 'parcel', text: 'Parcel'}, {value: 'freight', text: 'Freight (LTL)'}]"/>
            <b-form-checkbox v-if="mode === 'freight'" v-model="inboundFreight" switch>
              Coming to us (picked up here)
            </b-form-checkbox>
          </div>
        </modal-row>
        <modal-row :label="mode === 'freight' && inboundFreight ? 'Pickup Address' : 'Address'">
          <div class="float-right mb-3">
            <b-link @click="defaultaddr('domestic')">DOMESTIC</b-link>
            <b-link class="ml-3" @click="defaultaddr('international')">INTERNATIONAL</b-link>
          </div>
          <form-address ref="address" use-enter objkey="ship_" v-model="package"
                        :autofocus="!orderUrl"/>
          <b-form-checkbox class="mt-3" v-model="package.ship_residential" switch>Residential
          </b-form-checkbox>
        </modal-row>
        <modal-row label="Ship Date">
          <b-form-datepicker v-model="package.ship_date" size="sm"/>
        </modal-row>
      </template>
      <template v-else>
        <b-alert variant="warning" show size="sm" v-if="!!results && !!results.errors.length">
          <ul class="m-0 p-0">
            <li v-for="error in results.errors" :key="error" v-text="error"/>
          </ul>
        </b-alert>
        <mode-selector :modes="Object.keys(results.quotes)" v-model="carrier"/>
        <!-- freight comes back insured and uninsured when a value is typed: one set at a time -->
        <div class="d-flex flex-wrap align-items-center mt-2" v-if="hasInsured">
          <b-form-radio-group v-model="showInsured" class="mr-3"
                              :options="[{value: true, text: 'Insurance'}, {value: false, text: 'No Insurance'}]"/>
          <small :class="insurance.requested ? 'text-success' : 'text-muted'">
            {{ insurance.requested ? 'Insurance requested' : 'No insurance requested' }}<template
              v-if="insurance.requested && insurance.value"> ({{ insurance.value | money }})</template>
          </small>
        </div>
      </template>
    </template>
    <template #footer>
      <b-button size="sm" class="px-5 mr-2" @click="clear" variant="outline-secondary">Clear</b-button>
      <b-button size="sm" class="px-5" @click="quote" variant="primary" v-show="!results">Quote</b-button>
      <b-button size="sm" class="px-5 mr-2" @click="results = null" v-show="!!results"
                variant="outline-secondary">Edit
      </b-button>
    </template>

    <!-- the packages scroll with the body; the address stays in the strip above -->
    <template v-if="!results">
      <quote-units-table v-if="mode === 'parcel'" :value="parcels" @next="quote"/>
      <freight-quote-input v-else :value="freight" @next="quote"/>
    </template>

    <b-list-group v-if="!!results && !!results.quotes">
      <b-list-group-item v-for="ship in carrierQuotes" :variant="ship.ship ? '' : 'light'"
                         :key="ship.description + ship.pkg_type" href="#" @click="setShipping(ship)">
        <div class="d-flex">
          <h6 class="mr-auto">{{ ship.carrier }} - {{ ship.description }}
            <div v-if="ship.pkg_type" class="font-italic">{{ ship.pkg_type }}</div>
          </h6>
          <h6>{{ ship.rate | money }}</h6>
        </div>
        <div class="d-flex">
          <div class="font-italic mr-auto">
            Pickup <strong>{{ ship.shipDate_day }}</strong>
          </div>
          <div class="font-italic">
            Arrives <strong>{{ ship.deliverBy_day }}</strong>
          </div>
        </div>
        <div class="d-flex small text-muted" v-if="ship.freight_offer">
          <div class="mr-auto">
            {{ ship.freight_offer.scac }}
            <template v-if="ship.freight_offer.transit_days"> · {{ ship.freight_offer.transit_days }} day transit</template>
            <b-badge variant="success" class="ml-1" v-if="ship.freight_offer.guaranteed">Guaranteed</b-badge>
            <b-badge variant="info" class="ml-1" v-if="ship.freight_offer.insured_amount">Insured {{ ship.freight_offer.insured_amount | money }}<template v-if="ship.freight_offer.insurance_premium"> ({{ ship.freight_offer.insurance_premium | money }} incl.)</template></b-badge>
          </div>
          <div v-if="ship.freight_offer.expires">valid until {{ validUntil(ship.freight_offer.expires) }}</div>
        </div>
      </b-list-group-item>
    </b-list-group>
  </modal>
</template>

<script>

import formAddress from "../form/form-address.vue";
import FreightQuoteInput from "./freight-quote-input.vue";
import QuoteUnitsTable from "./quote-units-table.vue";
import {refreshServicesFor} from "@/lib/freight";

const now = new Date()

export default {
  name: "ship-quote",
  components: {
    FreightQuoteInput,
    QuoteUnitsTable,
    formAddress
  },

  data() {
    return {
      package: {},
      results: null,
      carrier: null,
      // parcel: one box's dims and weight; freight: pallets and the shipment's options
      mode: this.freightMode ? 'freight' : 'parcel',
      freight: {},
      parcels: [],
      showInsured: true,
      inboundFreight: !!this.inbound,
    }
  },

  props: {
    orderUrl: String,
    useOrder: Boolean,
    init: null,
    // a PO: freight comes to us from the address
    inbound: Boolean,
    freightMode: Boolean,
  },

  created() {
    this.clear();
    if (this.orderUrl || this.useOrder) {
      let order = null
      if (this.orderUrl) {
        this.asyncAction(this.orderUrl, 'GET').then((o) => this.fromOrder(o))
      } else if (this.$store.state.record.type == 'order') {
        order = this.$store.state.record.active
      }

      if (order)
        this.fromOrder(order)

    } else if (this.init) {
      this.package = {
        ...this.package,
        ...this.init
      }
    }
  },

  watch: {
    // residential: the freight site type and the residential charge follow the switch
    'package.ship_residential'(resi) {
      if (!this.freight.freight)
        this.$set(this.freight, 'freight', {})
      const f = this.freight.freight
      const key = this.inboundFreight ? 'residential_pickup' : 'residential_delivery'
      this.$set(f, 'accessorials', {...(f.accessorials || {}), [key]: !!resi})
      if (resi)
        this.$set(f, 'location_type', 'residential')
      else if (f.location_type === 'residential')
        this.$set(f, 'location_type', null)
    },
  },

  computed: {
    // freight offers cheapest first
    insurance() {
      return (this.results && this.results.insurance) || {}
    },
    hasInsured() {
      return Object.values((this.results && this.results.quotes) || {}).flat().some(this.insured)
    },
    carrierQuotes() {
      const all = (this.results && this.results.quotes && this.results.quotes[this.carrier]) || []
      const list = this.hasInsured ? all.filter(q => !q.freight_offer || this.insured(q) === this.showInsured) : all
      if (!list.some(q => q.freight_offer))
        return list
      return [...list].sort((a, b) => Number(a.rate) - Number(b.rate))
    },
  },

  methods: {
    defaultaddr(type) {
      this.asyncAction('/api/ship/quote/address', 'GET').then((r) => {
        this.package = {
          ...this.package,
          ...r[type]
        }
      })
    },

    clear() {
      this.package = {
        ship_country: 'US',
        ship_address1: null,
        ship_address2: null,
        ship_city: null,
        ship_state: null,
        ship_zip: null,
        pkg_height: null,
        pkg_width: null,
        pkg_length: null,
        pkg_weight: null,
        ship_residential: false,
        ship_date: now.getFullYear() + '-' + ((now.getMonth() + 1) < 10 ? '0' : '') + (now.getMonth() + 1) + '-' + now.getDate()
      }
      this.freight = {}
      this.parcels = []
      this.results = null
      this.$refs.address && this.$refs.address.focus()
    },

    /** the order's address, and what it already says: residential, and insured for its subtotal when Insure is set */
    fromOrder(order) {
      this.package = {
        ...this.package,
        ship_address1: order.ship_address1,
        ship_address2: order.ship_address2,
        ship_city: order.ship_city,
        ship_state: order.ship_state,
        ship_zip: order.ship_zip,
        ship_country: order.ship_country,
        ship_residential: !!order.ship_residential,
      }
      this.freight = {
        freight: {
          accessorials: order.ship_residential ? {residential_delivery: true} : {},
          location_type: order.ship_residential ? 'residential' : null,
          insurance: order.ship_insure
              ? {insured_value: Math.round(((order.payment_subtotal || 0) + (order.payment_auction || 0)) * 100) / 100}
              : {},
        }
      }
    },

    insured(q) {
      return !!(q.freight_offer && q.freight_offer.insured_amount)
    },

    validUntil(expires) {
      return new Date(expires).toLocaleString([], {weekday: 'short', hour: 'numeric', minute: '2-digit'})
    },

    async quote() {
      this.results = this.mode === 'freight'
          ? await this.asyncAction('/api/ship/quote', 'POST', {
            data: {
              ...this.package,
              mode: 'freight',
              units: this.freight.units,
              freight: this.freight.freight,
              return_shipment: this.inboundFreight,
            }
          })
          : await this.asyncAction('/api/ship/quote', 'POST', {
            data: {...this.package, units: this.parcels}
          })

      this.carrier = Object.keys(this.results.quotes)[0]
      this.showInsured = this.hasInsured && !!this.insurance.requested
      await refreshServicesFor(Object.values(this.results.quotes).flat().map(q => q.ship && q.ship.id))
    },

    async setShipping(ship) {
      // a rate on its own (Tools, a PO, a sales quote): nothing to assign it to
      if (!ship._links || !ship._links.assign_url)
        return
      await this.confirm('Assign all unshipped shipments to ' + ship.description + '?')
      const url = ship._links.assign_url
      await this.asyncAction(url.href, url.method)
      window.location.reload()
    }
  }
}
</script>

<style scoped>

</style>
<style>
/* wide enough for the freight table on one line, short of Bootstrap's xl (1140) */
.modal-dialog.ship-quote-dialog {
  max-width: 920px;
}
</style>
